import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * OptionsScalperMode — Micro-Trend Momentum Scalper
 *
 * How it works:
 * - Compares the latest live tick against the immediate preceding snapshot in history.
 * - Looks exclusively at the ATM ± 1 strikes.
 * - If a strike sees a sudden spike in BOTH Volume and OI simultaneously between just these two ticks,
 *   it fires a high-speed SCALP BUY momentum signal.
 */
export const evaluateOptionsScalperMode: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    // We need at least 2 ticks to compare
    if (fullTrendData.length < 2) return null;

    const liveRow = fullTrendData[0];
    const prevRow = fullTrendData[1];
    
    const { symbol, expiry, gap } = context;
    const spot = liveRow.spotPrice || liveRow.index_ltp || liveRow.ltp || data.spotPrice || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = liveRow.flatOpts || extractSnapshotOptions(liveRow, symbol, expiry) || [];
    const prevRawOptsArr = prevRow.flatOpts || extractSnapshotOptions(prevRow, symbol, expiry) || [];
    
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    const prevFlatOpts = ensureFlatOpts(prevRawOptsArr);
    
    if (!liveFlatOpts.length || !prevFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    // For scalping, strictly watch ATM, ITM1, and OTM1
    const scalpStrikes = [atmStrike - gap, atmStrike, atmStrike + gap];

    let bestScore = 0;
    let bestSignal: SignalResult | null = null;

    for (const strike of scalpStrikes) {
        for (const type of ['CE', 'PE'] as const) {
            const liveOpt = liveFlatOpts.find((o: any) => o.strike === strike && o.type === type);
            const prevOpt = prevFlatOpts.find((o: any) => o.strike === strike && o.type === type);
            
            if (liveOpt && prevOpt) {
                const volSpike = (liveOpt.volume || 0) - (prevOpt.volume || 0);
                const oiSpike = (liveOpt.oi || 0) - (prevOpt.oi || 0);
                
                // Micro-momentum: both volume and OI must jump aggressively in a single tick
                // Thresholds are arbitrary, based on typical index movement speed
                const volThreshold = gap >= 100 ? 50000 : 150000;
                const oiThreshold = gap >= 100 ? 5000 : 15000;
                
                if (volSpike > volThreshold && oiSpike > oiThreshold) {
                    const score = volSpike + (oiSpike * 5); // Weight OI addition heavily
                    
                    if (score > bestScore) {
                        bestScore = score;
                        bestSignal = {
                            trade_type: type === 'CE' ? 'BULL' : 'BEAR',
                            strike: strike,
                            option_type: type,
                            entry_price: liveOpt.ltp || liveOpt.price || liveOpt.close || 0,
                            remarks: `SCALP MOMENTUM: +${volSpike.toLocaleString('en-IN')} Vol / +${oiSpike.toLocaleString('en-IN')} OI`
                        };
                    }
                }
            }
        }
    }

    if (!bestSignal || bestSignal.entry_price <= 0) return null;
    return bestSignal;
};
